import { useDebounce, downloadXLSFile, sendGAEvent } from 'utils/utils';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { postToast } from 'features/common/utils/postFlash';

import { IdParamsType } from 'utils/types';
import { PunchupViewResponsesTable } from './PunchupViewResponsesTable';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupSearch } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSearch';
import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { PunchupFilterSelect } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupFilterSelect';
import { isBackgroundRefetch } from 'features/common/utils/queryStatus';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { getData } from 'services/api';
import { useEventCustomFields } from 'features/PunchupPages/CustomFieldsResponse/hooks/useEventCustomFields';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTranslation } from 'react-i18next';
import { SYSTEM_FIELD_NAMES } from 'features/PunchupPages/CustomFieldsResponse/components/PunchupResponsesTable/constants';

const PAGE_NAME = 'View Responses';
const ALL_QUESTIONS = '__all__';

export const PunchupViewResponses = () => {
	const { t } = useTranslation();
	const { event } = useGetEvent();
	const { id } = useParams<IdParamsType>();
	const { setSearchText, responsesQueryObject, sortBy, order, handleSort } = useEventCustomFields(false);
	const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);

	const searchDebounce = useDebounce(setSearchText);

	const questionOptions = useMemo(() => {
		if (!responsesQueryObject.data) return [];
		const names = new Set<string>();
		responsesQueryObject.data.pages.forEach((page) => {
			page.forEach((data) => {
				data.responses.forEach((r) => {
					if (!SYSTEM_FIELD_NAMES.includes(r.custom_field.name)) {
						names.add(r.custom_field.name);
					}
				});
			});
		});
		return [
			{ label: t('viewResponses-filter-allQuestions', 'All Questions'), value: ALL_QUESTIONS },
			...Array.from(names).map((name) => ({ label: name, value: name })),
		];
	}, [responsesQueryObject.data, t]);

	const handleDownload = async () => {
		setIsDownloading(true);
		try {
			const response = await getData(`/events/${id}/custom-fields/responses/download`, {
				params: { all_attendees: false },
				responseType: 'blob',
				headers: { 'Content-Type': 'blob' },
			});

			let fileName = event?.name ? event.name.replace(/[^a-zA-Z0-9]/g, ' ') : 'Responses';
			if (event?.startDate) {
				const timezone = event.timeZone || 'UTC';
				const startDateTime = DateTime.fromISO(event.startDate, { zone: timezone });
				const formattedDate = startDateTime.toFormat('MMM dd hh:mm a').replace(/:/g, '.');
				fileName += ` (${formattedDate})`;
			}

			downloadXLSFile(response, fileName);
			sendGAEvent({ category: 'Custom Fields', action: 'Custom Fields Responses download' });
		} catch {
			postToast(
				'error',
				t('customFields-responses-download-error', 'Could not export attendee data. Please try again.')
			);
		} finally {
			setIsDownloading(false);
		}
	};

	const handleRefresh = () => {
		responsesQueryObject.refetch();
		sendGAEvent({ category: 'Custom Fields', action: 'View Responses refresh' });
	};

	return (
		<PunchupTablePage
			pageName={PAGE_NAME}
			header={
				<PunchupTablePageHeader
					title={PAGE_NAME}
					description="Review what each guest answered to the questions you set on this show."
					onDownload={handleDownload}
					downloadLabel={t('customFields-responses-download')}
					isDownloading={isDownloading}
					onRefresh={handleRefresh}
					refreshLabel={t('button-refresh', 'Refresh')}
					isRefreshing={responsesQueryObject.isFetching}
				/>
			}
			search={
				<PunchupSearch onChange={(e) => searchDebounce(e.target.value)} placeholder={t('search-email-or-name')} />
			}
			controlsActions={
				questionOptions.length > 1 ? (
					<PunchupFilterSelect
						options={questionOptions}
						value={selectedQuestion ?? ALL_QUESTIONS}
						onChange={(val) => setSelectedQuestion(val === ALL_QUESTIONS ? null : val)}
					/>
				) : undefined
			}
		>
			{responsesQueryObject.isError ? (
				<PunchupErrorState
					title={t('responses-load-error', 'Failed to load attendee data')}
					description={t('responses-load-error-description', 'We could not load the attendee data. Please try again.')}
					onRetry={() => responsesQueryObject.refetch()}
				/>
			) : (
				<PunchupViewResponsesTable
					responsesQueryObject={responsesQueryObject}
					selectedQuestion={selectedQuestion}
					sortBy={sortBy}
					order={order}
					onSort={handleSort}
					isRefetching={isBackgroundRefetch(responsesQueryObject)}
				/>
			)}
		</PunchupTablePage>
	);
};
